import React, { useMemo, useState } from 'react';
import { Share2 } from 'lucide-react';
import { formatPct, shortDate } from '../../lib/format';
import { convertedCost, convertedMarketValue, fxForSource } from '../../lib/currency';
import { useSort, SortableTh } from '../SortableTable';
import TableSearch from '../TableSearch';
import { MoneyCell, PnLMoney } from './MoneyMode';

// Source Summary — one row per source, showing rows / cost / mv / P&L across all
// selected reporting dates. Helps see which sources are dragging or leading.
const SourceSummaryTable = ({ snapshots, holdings, sources, currency, fxRates = {} }) => {
  const [query, setQuery] = useState('');

  const perSource = useMemo(() => {
    return sources.map((src) => {
      const bySnap = snapshots.map((s) => {
        const srcRows = (holdings[s.id] || {})[src.id] || [];
        const fx = fxForSource(fxRates, s.id, src.id);
        let cost = 0, mv = 0;
        srcRows.forEach((r) => {
          cost += convertedCost(r, fx);
          mv += convertedMarketValue(r, fx);
        });
        return {
          snap: s,
          rowCount: srcRows.length,
          cost,
          marketValue: mv,
          pnl: mv - cost,
          pnlPct: cost > 0 ? ((mv - cost) / cost) * 100 : 0,
        };
      });
      const totalRows = bySnap.reduce((a, x) => a + x.rowCount, 0);
      const lastMv = bySnap[bySnap.length - 1]?.marketValue || 0;
      return { source: src, bySnap, totalRows, lastMv };
    });
  }, [sources, snapshots, holdings, fxRates]);

  const { sort, onSort, sortRows } = useSort({ key: 'name', dir: 'asc' });
  const filtered = perSource.filter(({ source }) =>
    !query.trim() || source.name.toLowerCase().includes(query.trim().toLowerCase()),
  );
  const sorted = sortRows(filtered, {
    name: (r) => r.source.name,
    mv: (r) => r.lastMv,
    rows: (r) => r.totalRows,
  });

  const snapTotals = snapshots.map((s, idx) => {
    let rowCount = 0, cost = 0, mv = 0;
    filtered.forEach(({ bySnap }) => {
      rowCount += bySnap[idx].rowCount;
      cost += bySnap[idx].cost;
      mv += bySnap[idx].marketValue;
    });
    return { snap: s, rowCount, cost, marketValue: mv, pnl: mv - cost, pnlPct: cost > 0 ? ((mv - cost) / cost) * 100 : 0 };
  });

  const colsPerSnap = 5;

  return (
    <div>
      <TableSearch
        value={query}
        onChange={setQuery}
        placeholder="Search source…"
        testId="search-source-summary"
        stats={query ? `${filtered.length} / ${perSource.length}` : null}
      />
      <div className="overflow-x-auto border border-border bg-white">
        <table className="grid-table" data-testid="source-summary-table">
          <thead>
            <tr>
              <SortableTh sortKey="name" sort={sort} onSort={onSort} rowSpan={2} className="min-w-[220px]">Source</SortableTh>
              {snapshots.map((s) => (
                <th key={s.id} colSpan={colsPerSnap} className="text-center">
                  <div className="inline-flex items-center gap-1 justify-center">
                    {s.label}
                    {s.shared_from && (
                      <span className="chip chip-warning text-[8px] !py-0 !px-1 leading-none" title={`Shared by ${s.shared_from.email} (${s.shared_role})`}>
                        <Share2 size={8} /> shared
                      </span>
                    )}
                  </div>
                  <div className="text-[10px] font-normal text-[#787872] font-mono normal-case tracking-normal mt-1">{shortDate(s.date)}</div>
                </th>
              ))}
            </tr>
            <tr>
              {snapshots.map((s, idx) => (
                <React.Fragment key={s.id}>
                  {idx === snapshots.length - 1
                    ? <SortableTh sortKey="rows" sort={sort} onSort={onSort} numeric>Rows</SortableTh>
                    : <th className="num">Rows</th>}
                  <th className="num">Cost</th>
                  {idx === snapshots.length - 1
                    ? <SortableTh sortKey="mv" sort={sort} onSort={onSort} numeric>Mkt Val</SortableTh>
                    : <th className="num">Mkt Val</th>}
                  <th className="num">P&amp;L</th>
                  <th className="num">P&amp;L %</th>
                </React.Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 && (
              <tr><td colSpan={1 + snapshots.length * colsPerSnap} className="text-center py-6 text-[#AAA] text-xs">No sources match your search.</td></tr>
            )}
            {sorted.map(({ source, bySnap }) => (
              <tr key={source.id} data-testid={`source-summary-row-${source.id}`}>
                <td className="font-heading font-semibold">
                  {source.name}
                  {source.shared_from && (
                    <span className="chip chip-warning text-[8px] ml-2" title={`Shared by ${source.shared_from.email}`}>
                      <Share2 size={8} /> shared
                    </span>
                  )}
                </td>
                {bySnap.map((cell) => (
                  <React.Fragment key={cell.snap.id}>
                    <td className="num text-[#575757]">{cell.rowCount}</td>
                    <td className="num"><MoneyCell value={cell.cost} currency={currency} /></td>
                    <td className="num"><MoneyCell value={cell.marketValue} currency={currency} /></td>
                    <td className="num"><PnLMoney value={cell.pnl} currency={currency} /></td>
                    <td className={`num ${cell.pnlPct >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{formatPct(cell.pnlPct)}</td>
                  </React.Fragment>
                ))}
              </tr>
            ))}
            <tr style={{ background: '#F6F6F4' }}>
              <td className="font-heading font-bold">TOTAL</td>
              {snapTotals.map((t) => (
                <React.Fragment key={t.snap.id}>
                  <td className="num font-semibold text-[#575757]">{t.rowCount}</td>
                  <td className="num font-semibold"><MoneyCell value={t.cost} currency={currency} /></td>
                  <td className="num font-semibold"><MoneyCell value={t.marketValue} currency={currency} /></td>
                  <td className="num font-semibold"><PnLMoney value={t.pnl} currency={currency} /></td>
                  <td className={`num font-semibold ${t.pnlPct >= 0 ? 'pnl-positive' : 'pnl-negative'}`}>{formatPct(t.pnlPct)}</td>
                </React.Fragment>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SourceSummaryTable;
